import {
  Button,
  Collapse,
  Fade,
  Stack,
  Typography,
} from '@mui/material';
import { AlertContext, AuthContext } from 'constants/contexts';
import React, {
  useContext, useEffect, useMemo, useState, useCallback,
} from 'react';
import useIsDesktop from 'hooks/useIsDesktop';
import useFetchedData from 'hooks/useFetchedData';
import { EMPTY_OBJECT } from 'constants';
import { Link, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import Icon from './Icon';
import AppProvider from './AppProvider';

const POSITIONS = {
  MDP: 'mdp',
  LOGIN: 'login',
  CHECKOUT: 'checkout',
  PROFILE: 'profile',
};
function AlertProvider({ children }) {
  const [alerts, setAlerts] = useState({});
  const { pathname } = useLocation();
  const { isLoggedIn } = useContext(AuthContext);

  const { data: needAttentionOrders, mutate: refresh } = useFetchedData(isLoggedIn && {
    url: 'v1/orders/customer-orders/',
    query: {
      query: '{id,payment}',
      page: 1,
      'need-attention': true,
    },
  }, ({ data = EMPTY_OBJECT } = EMPTY_OBJECT) => data.results || []);

  useEffect(() => {
    refresh();
  }, [pathname, refresh]);

  const addAlert = useCallback((position, element) => {
    setAlerts((c) => {
      const id = Math.random().toString(36).substring(2, 15);
      return {
        ...c,
        [position]: [
          ...(c[position] || []),
          {
            id,
            element,
          },
        ],
      };
    });
  }, []);

  useEffect(() => {
    if (needAttentionOrders.length === 0) {
      return;
    }
    const unpaidOrder = needAttentionOrders.find((o) => o.payment.status_label === 'Failed');
    if (unpaidOrder) {
      const unpaidAlert = (
        <Alert
          title="Incomplete payment on a recent order."
          actionProps={{
            component: Link,
            to: `/order/${unpaidOrder.id}`,
          }}
          actionText="Update payment"
        />
      );
      addAlert(POSITIONS.MDP, unpaidAlert);
      addAlert(POSITIONS.PROFILE, unpaidAlert);
    }
    const quotedOrder = needAttentionOrders.find((o) => o.payment.status_label === 'Quoted');
    if (quotedOrder) {
      const quotedAlert = (
        <Alert
          title={`One order quoted, expires on ${dayjs(quotedOrder.payment.attention_due_date).format('MMM D [at] h a')}.`}
          actionProps={{
            component: Link,
            to: `/order/${quotedOrder.id}`,
          }}
          actionText="Order details"
          sx={{
            bgcolor: 'info.surface',
          }}
        />
      );
      addAlert(POSITIONS.MDP, quotedAlert);
      addAlert(POSITIONS.PROFILE, quotedAlert);
    }
    return () => { setAlerts({}); };
  }, [needAttentionOrders, addAlert]);

  const contextValue = useMemo(() => ({
    getAlerts(position) {
      return alerts[position] || [];
    },
    refresh,
  }), [alerts, refresh]);

  return (
    <AlertContext.Provider value={contextValue}>
      {children}
    </AlertContext.Provider>
  );
}

function Alert({
  title,
  actionProps,
  actionText,
  sx,
  ...props
}) {
  const isDesktop = useIsDesktop();
  const isScrollAtTop = AppProvider.useIsScrollAtTop();

  return (
    <Stack
      px={2}
      py={1.5}
      direction="row"
      justifyContent="space-between"
      sx={(theme) => ({
        bgcolor: 'error.surface',
        borderRadius: {
          xs: 0,
          md: 3,
        },
        ...sx,
      })}
      {...props}
    >
      <Stack
        alignItems="center"
        justifyContent="space-between"
        direction="row"
        flexWrap="wrap"
        flex={1}
        gap={0.5}
      >
        <Typography variant="title-sm">{title}</Typography>
        <Collapse in={isDesktop || isScrollAtTop} unmountOnExit>
          <Button
            variant="muted"
            size="small"
            endIcon={<Icon name="arrow-withtale-right" />}
            {...actionProps}
          >
            {actionText}
          </Button>
        </Collapse>
      </Stack>
      <Fade in={!isDesktop && !isScrollAtTop} direction="left" unmountOnExit>
        <Icon
          name="arrow-withtale-right"
          mt={0.25}
          {...actionProps}
        />
      </Fade>
    </Stack>
  );
}

AlertProvider.POSITIONS = POSITIONS;
AlertProvider.useAlerts = (position) => {
  const { getAlerts } = useContext(AlertContext);
  return getAlerts(position).map(({ id, element }) => React.cloneElement(element, { key: id }));
};

export default AlertProvider;
